import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) return { ok: false, error: error.message };
    return { ok: true, data };
  };

  const register = async (name, email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
        },
      },
    });

    if (error) return { ok: false, error: error.message };
    return { ok: true, data };
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();

    if (error) return { ok: false, error: error.message };

    setUser(false);
    return { ok: true };
  };

  const updateUser = (updates) => {
    setUser((current) => {
      if (!current) return current;
      return { ...current, ...updates };
    });
  };

  useEffect(() => {
    let isMounted = true;

    const getSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();

        if (error) {
          console.error("Session load error", error);
        }

        if (!isMounted) return;

        if (data?.session?.user) {
          setUser(data.session.user);
        } else {
          setUser(false);
        }
      } catch (error) {
        console.error("Session load error", error);
        if (isMounted) setUser(false);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    getSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;

      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(false);
      }

      setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}